// @flow

import { getSession, getBroadcastSetting } from "./../graphql/queries";
import { onUpdateBroadcastSetting } from "./../graphql/subscriptions";
import { API } from "aws-amplify";
import React, { useState, useEffect } from "react";
import MessagesView from "./MessagesView.react";
import {
  Container,
  Row,
  Col,
  ToggleButton,
  ButtonGroup,
} from "react-bootstrap";
import * as Sentry from "@sentry/react";
import NavigationBar from "./NavigationBar.react";

import Card from "react-bootstrap/Card";
import ChatInputView from "./ChatInputView.react";
import SessionUsersView from "./SessionUsersView.react";
import ShortcutsView from "./ShortcutsView.react";
import SheetsView from "./SheetsView.react";
import AutoTranslateModule from "./AutoTranslateModule.react";

type Props = $ReadOnly<{
  sessionId: ?string,
}>;

const styles = {
  // 경고는 하되 시야를 잡아먹지 않게 — 채운 배지 대신 테두리만
  demoTextBadge: {
    display: "inline-flex",
    alignItems: "center",
    verticalAlign: "middle",
    marginLeft: "0.6rem",
    gap: "0.35rem",
    padding: "0.15rem 0.5rem",
    border: "1px solid rgba(255, 138, 138, 0.7)",
    borderRadius: "999px",
    color: "#ff8a8a",
    fontSize: "0.75rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  // 녹화 표시처럼 점 하나
  demoTextDot: {
    flexShrink: 0,
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "#ff6b6b",
  },
};

export default function TranslatorView(props: Props): React$Element<any> {
  const [sessionName, setSessionName] = useState("");
  const [broadcastSetting, setBroadcastSetting] = useState(null);

  const [showSheets, setShowSheets] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [sheetsChecked, setSheetsChecked] = useState(false);
  const [shortcutsChecked, setShortcutsChecked] = useState(false);

  const [shortcutInput, setShortcutInput] = useState("");
  const [shortcutInputIndex, setShortcutInputIndex] = useState(0);

  const [sourceWord, setSourceWord] = useState("");
  const [translatedWord, setTranslatedWord] = useState("");

  const [selectedUser, setSelectedUser] = useState("");

  let inputWidth;
  let sheetsWidth;
  let shortcutsWidth;

  if (!showSheets && !showShortcuts) {
    inputWidth = 12;
  } else if (showSheets && !showShortcuts) {
    sheetsWidth = 7;
    inputWidth = 5;
  } else if (!showSheets && showShortcuts) {
    inputWidth = 10;
    shortcutsWidth = 2;
  } else if (showSheets && showShortcuts) {
    sheetsWidth = 6;
    inputWidth = 4;
    shortcutsWidth = 2;
  }

  useEffect(() => {
    fetchSession();
  }, [props.sessionId]);

  useEffect(() => {
    fetchBroadcastSetting();
  }, []);

  // Broadcast Settings 화면에서 설정을 바꾸면 이 화면에도 바로 반영한다
  // (Demo Text 배지가 실제 설정과 어긋나면 오히려 혼란스럽다)
  useEffect(() => {
    let isMounted = true;
    let subscription;

    const subscribe = () => {
      // 기존 구독이 남아있을 수 있으니 먼저 정리
      subscription?.unsubscribe?.();

      const obs = API.graphql({ query: onUpdateBroadcastSetting });

      subscription = obs.subscribe({
        next: ({ value }) => {
          // 세션 필터가 없는 구독이라 다른 세션 이벤트도 들어온다
          if (value?.data?.onUpdateBroadcastSetting?.id !== props.sessionId) {
            return;
          }
          fetchBroadcastSetting();
        },
        error: (err) => {
          console.warn("[TranslatorView] subscription error", err);
          const message =
            err?.error?.errors?.[0]?.message ?? err?.errors?.[0]?.message;
          Sentry.captureException(
            new Error(message ?? "TranslatorView subscription error"),
            {
              extra: {
                context: "TranslatorView onUpdateBroadcastSetting subscription",
                rawError: err,
              },
            }
          );
          if (!isMounted) return;

          if (message === "Connection closed") {
            setTimeout(() => {
              if (isMounted) {
                console.log(
                  "[TranslatorView] Trying to reestablish subscription..."
                );
                subscribe();
              }
            }, 1000);
          }
        },
      });
    };

    subscribe();

    return () => {
      isMounted = false;
      subscription?.unsubscribe?.();
    };
  }, [props.sessionId]);

  useEffect(() => {
    window.addEventListener("keydown", handlePanelHotkeyPress);
    return () => {
      window.removeEventListener("keydown", handlePanelHotkeyPress);
    };
  }, []);

  async function fetchSession() {
    const apiData = await API.graphql({
      query: getSession,
      variables: {
        id: props.sessionId,
      },
    });
    setSessionName(apiData.data.getSession.name);
  }

  async function fetchBroadcastSetting() {
    const apiData = await API.graphql({
      query: getBroadcastSetting,
      variables: {
        id: props.sessionId,
      },
    });
    setBroadcastSetting(apiData.data.getBroadcastSetting);
  }

  function handleShowSheets() {
    setSheetsChecked((prevSheetsChecked) => !prevSheetsChecked);
    setShowSheets((prevShowSheets) => !prevShowSheets);
  }

  function handleShowShortcuts() {
    setShortcutsChecked((prevShortcutsChecked) => !prevShortcutsChecked);
    setShowShortcuts((prevShowShortcuts) => !prevShowShortcuts);
  }

  // Cmd(Ctrl) + B: Script 패널, Cmd(Ctrl) + /: Shortcuts 패널
  // 입력창에 포커스가 있어도 동작하도록 window에 등록한다
  function handlePanelHotkeyPress(event) {
    if (!event.ctrlKey && !event.metaKey) return;
    if (event.altKey || event.shiftKey) return;

    const key = event.key?.toLowerCase();
    if (key === "b") {
      event.preventDefault();
      handleShowSheets();
    } else if (key === "/") {
      event.preventDefault();
      handleShowShortcuts();
    }
  }

  if (props.sessionId == null) {
    return <div>Invalid Session</div>;
  }
  return (
    <>
      <NavigationBar />
      <Container fluid>
        <Row>
          <Col className="p-0">
            <Card className="m-2 mb-0" bg="secondary" text="white">
              <Card.Body className="p-2 m-0">
                <Card.Text className="ms-1" as="h5">
                  {sessionName}
                  {/* 켜져 있을 때만 보여준다 — 상시 노출되면 눈에 안 들어온다 */}
                  {broadcastSetting?.isDemoText === true ? (
                    <span
                      style={styles.demoTextBadge}
                      title="Demo Text가 켜져 있어 자막이 사라지지 않습니다"
                    >
                      <span style={styles.demoTextDot} />
                      Demo Text ON
                    </span>
                  ) : null}
                  <ButtonGroup className="float-end">
                    <ToggleButton
                      id="toggle-setCheck"
                      type="checkbox"
                      size="sm"
                      style={{ fontSize: "0.9rem" }}
                      variant="outline-warning"
                      checked={sheetsChecked}
                      value="1"
                      onChange={handleShowSheets}
                    >
                      Script
                    </ToggleButton>
                    <ToggleButton
                      id="toggle-setCheckShortcuts"
                      type="checkbox"
                      size="sm"
                      style={{ fontSize: "0.9rem" }}
                      variant="outline-warning"
                      checked={shortcutsChecked}
                      value="1"
                      onChange={handleShowShortcuts}
                    >
                      Shortcuts
                    </ToggleButton>
                  </ButtonGroup>
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row className="p-0">
          {showSheets ? (
            <Col xs={sheetsWidth} className="p-2">
              <SheetsView
                sessionId={props.sessionId}
                selectedUser={selectedUser}
                sessionName={sessionName}
              />
            </Col>
          ) : null}
          <Col xs={inputWidth} className="p-2">
            <MessagesView
              sessionId={props.sessionId}
              title="History"
            ></MessagesView>
            <br />
            <SessionUsersView
              sessionId={props.sessionId}
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
            />
            <br />
            <ChatInputView
              sessionId={props.sessionId}
              shortcutInput={shortcutInput}
              shortcutInputIndex={shortcutInputIndex}
              setSourceWord={setSourceWord}
              translatedWord={translatedWord}
            />
            {/* <AutoTranslateModule
              sessionId={props.sessionId}
              sourceWord={sourceWord}
              setTranslatedWord={setTranslatedWord}
              title="Auto Translate"
            /> */}
          </Col>
          {showShortcuts ? (
            <Col xs={shortcutsWidth} className="p-2">
              <ShortcutsView
                sessionId={props.sessionId}
                setShortcutInput={setShortcutInput}
                setShortcutInputIndex={setShortcutInputIndex}
              />
            </Col>
          ) : null}
        </Row>
      </Container>
    </>
  );
}
